import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Handshake, Users, Buildings, HandCoins, Lightbulb, Scales, Leaf, Bank, Star, ArrowRight, ArrowLeft, MapPin, Envelope, Globe, WhatsappLogo, SignIn, DeviceMobile, Quotes } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';
import { ParallaxScene, ParallaxLayer } from '../components/motion/ParallaxScene';
import { TiltCard } from '../components/motion/TiltCard';
import MissionsShowcase, { MISSIONS, IllustrationAvatar } from '../components/public/MissionsShowcase';
import CookieConsent, { isTrackingAllowed } from '../components/public/CookieConsent';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  network: Handshake, growth: HandCoins, support: Lightbulb, vote: Scales,
  impact: Leaf, finance: Bank, star: Star,
};

const COLLEGE_TONES = {
  C: 'bg-[#00AEEF] text-white',
  D: 'bg-[#FF8E00] text-white',
  F: 'bg-[#EA3C3A] text-white',
  G: 'bg-[#80C51F] text-white',
};

// Citations ultramarines — les avatars sont des crops des illustrations officielles
// (mission = référence dans MISSIONS, stepIndex = index de l'étape dont le crop cible le visage)
const ULTRAMARIN_QUOTES = [
  {
    text: "Ensemble, nous transformons nos savoir-faire créoles en coopératives qui font vivre nos quartiers.",
    author: "Marie-Claire Latou",
    role: "Artisane coopératrice — Guadeloupe",
    accent: '#EA3C3A',
    mission: 'agro', stepIndex: 0,
  },
  {
    text: "La mer nous sépare mais la coopération nous unit — Fort-de-France, Cayenne, Saint-Denis, un même élan.",
    author: "Jean-Michel Césaire",
    role: "Coopérateur PM — Martinique",
    accent: '#00AEEF',
    mission: 'agro', stepIndex: 2,
  },
  {
    text: "En Guyane, la forêt et les fleuves nourrissent une économie solidaire enracinée dans nos communautés.",
    author: "Sarah Diomandé",
    role: "Entrepreneure sociale — Guyane",
    accent: '#80C51F',
    mission: 'agro', stepIndex: 1,
  },
  {
    text: "À La Réunion, la mixité culturelle est notre force : chaque coopérateur enrichit le projet commun.",
    author: "Ravi Payet",
    role: "Coopérateur PP — La Réunion",
    accent: '#FF8E00',
    mission: 'logi', stepIndex: 0,
  },
  {
    text: "À Mayotte, coopérer, c'est bâtir un modèle économique qui respecte nos traditions et prépare demain.",
    author: "Anziza Hamada",
    role: "Coopératrice contributrice — Mayotte",
    accent: '#D5007D',
    mission: 'logi', stepIndex: 1,
  },
  {
    text: "Saint-Martin et Saint-Barthélémy avancent ensemble : la coopérative est notre trait d'union caribéen.",
    author: "Yannick Fleming",
    role: "Prestataire coopérateur — Îles du Nord",
    accent: '#00AEEF',
    mission: 'logi', stepIndex: 2,
  },
  {
    text: "Coopérer aujourd'hui en Outre-mer, c'est refuser la fatalité et construire des territoires plus forts.",
    author: "Élise Andriana",
    role: "Direction — Réseau O'SCOP Outremer",
    accent: '#2D5A4C',
    mission: 'agro', stepIndex: 3,
  },
];

function UltramarinBanner() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ULTRAMARIN_QUOTES.length), 6000);
    return () => clearInterval(t);
  }, []);
  const q = ULTRAMARIN_QUOTES[idx];
  return (
    <section className="max-w-6xl mx-auto px-6 mt-14" data-testid="ultramarin-banner">
      <div className="relative rounded-2xl border border-[#E8E6E1] bg-gradient-to-br from-white via-[#F9F8F5] to-white overflow-hidden shadow-sm">
        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: q.accent, transition: 'background 700ms ease' }} />
        <div className="flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-7 px-6 md:px-10 py-6 md:py-8">
          <div className="relative flex-shrink-0" data-testid="ultramarin-photo-wrap">
            {(() => {
              const m = MISSIONS.find((x) => x.key === q.mission) || MISSIONS[0];
              const s = (m.steps || [])[q.stepIndex] || {};
              const cx = s.crop_cx ?? s.crop?.cx ?? 50;
              const cy = s.crop_cy ?? s.crop?.cy ?? 50;
              return (
                <span key={idx} className="motion-fade-up inline-block">
                  <IllustrationAvatar
                    src={m.image}
                    cx={cx}
                    cy={cy}
                    zoom={5.5}
                    size={88}
                    ring={q.accent}
                    testId="ultramarin-photo"
                  />
                </span>
              );
            })()}
            <div
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center bg-white shadow-md"
              style={{ transition: 'color 700ms ease' }}
            >
              <Quotes size={16} weight="fill" style={{ color: q.accent, transition: 'color 700ms ease' }} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p
              key={`t-${idx}`}
              className="text-base md:text-lg italic text-[#1A2421] leading-relaxed motion-fade-up"
              data-testid="ultramarin-quote-text"
              style={{ fontFamily: 'Manrope' }}
            >
              « {q.text} »
            </p>
            <p className="mt-2 text-sm font-extrabold" style={{ color: q.accent, transition: 'color 700ms ease', fontFamily: 'Manrope' }} data-testid="ultramarin-quote-author">
              {q.author}
            </p>
            <p className="text-[11px] uppercase tracking-widest text-[#5E6D66] font-semibold" data-testid="ultramarin-quote-role">
              {q.role}
            </p>
          </div>
          <div className="flex gap-1.5" role="tablist" aria-label="Sélectionner une citation">
            {ULTRAMARIN_QUOTES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="w-2 h-2 rounded-full transition-all"
                style={{ background: i === idx ? q.accent : '#E8E6E1', transform: i === idx ? 'scale(1.4)' : 'scale(1)' }}
                aria-label={`Citation ${i + 1}`}
                data-testid={`ultramarin-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function JoinCooperativePage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/public/landing`).then((r) => setData(r.data)).catch(() => setData(null));
    // Track page_view uniquement si consentement accordé
    const fireTrack = () => {
      if (isTrackingAllowed()) {
        axios.post(`${API}/api/public/landing/track`, { event: 'page_view' }).catch(() => {});
      }
    };
    fireTrack();
    const onConsent = (e) => { if (e?.detail?.value === 'granted') fireTrack(); };
    window.addEventListener('oscop:cookie-consent', onConsent);
    return () => window.removeEventListener('oscop:cookie-consent', onConsent);
  }, []);
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F5]"><p className="text-[#5E6D66]">Chargement…</p></div>;
  }
  return (
    <>
      <JoinCooperativeView data={data} />
      <CookieConsent />
    </>
  );
}

export function JoinCooperativeView({ data }) {
  // Track un événement de conversion sur la variante A/B en cours (silencieux, gated par consentement)
  const trackCTA = useCallback((event = 'cta_click') => {
    if (!isTrackingAllowed()) return;
    const vid = data?._variant?.id;
    axios.post(`${API}/api/public/landing/track`, { variant_id: vid || undefined, event }).catch(() => {});
  }, [data]);

  if (!data) return null;
  return (
    <div className="min-h-screen bg-[#F9F8F5] text-[#1A2421]" data-testid="join-cooperative-page" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {/* ============ HERO ============ */}
      <ParallaxScene className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1748810859309-b943147cdda2?w=1600&q=70')" }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(45,90,76,0.86) 0%, rgba(0,174,239,0.55) 100%)' }} />
        <ParallaxLayer depth={0.08} className="parallax-blob parallax-blob-cyan" style={{ top: '10%', right: '10%', width: 280, height: 280 }} />
        <ParallaxLayer depth={0.06} className="parallax-blob parallax-blob-lime" style={{ bottom: '10%', left: '5%', width: 220, height: 220 }} />

        <div className="relative max-w-6xl mx-auto px-6 pt-4 md:pt-5">
          <a
            href="https://objectifscopoutremer.com"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-xs font-semibold uppercase tracking-widest transition-colors group"
            data-testid="hero-back-ecosystem"
            title="Revenir sur objectifscopoutremer.com"
          >
            <span className="w-8 h-8 rounded-full bg-white/10 backdrop-blur border border-white/30 flex items-center justify-center group-hover:bg-white/20 group-hover:-translate-x-0.5 transition-all">
              <ArrowLeft size={14} weight="bold" />
            </span>
            objectifscopoutremer.com
          </a>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-14 md:py-20 grid md:grid-cols-2 gap-8 items-center">
          <div className="motion-fade-up motion-fade-up-delay-0 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex gap-1">
                {['#EA3C3A', '#00AEEF', '#80C51F', '#FF8E00', '#D5007D'].map((c) => (
                  <span key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />
                ))}
              </div>
              <span className="text-xs uppercase tracking-widest opacity-80">Objectif SCOP Outremer</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight" style={{ fontFamily: 'Manrope' }}>
              {data.title}
            </h1>
            <p className="mt-3 text-lg italic opacity-95">« {data.hero?.tagline} »</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold uppercase tracking-wider">
              {(data.subtitle_steps || []).map((s, i) => (
                <span key={s} className="flex items-center gap-2" data-testid={`hero-step-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white/70" /> {s}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={data.cta?.href || '/register'}
                onClick={() => trackCTA('cta_click_hero')}
                className="inline-flex items-center gap-2 bg-[#FF8E00] hover:bg-[#e07d00] transition-colors px-6 py-3 rounded-full font-bold shadow-xl motion-shine"
                data-testid="join-cta-primary"
              >
                {data.cta?.label || 'Rejoindre la coopérative'} <ArrowRight weight="bold" />
              </Link>
              <a href="#comment" onClick={() => trackCTA('scroll_comment')} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 px-5 py-3 rounded-full font-semibold">
                Comment ça marche ?
              </a>
              <button
                onClick={() => {
                  trackCTA('share_whatsapp');
                  const shareUrl = window.location.origin + '/';
                  const text = `Découvrez Objectif SCOP Outremer — la coopérative des territoires ultramarins : ${shareUrl}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] transition-colors text-white px-5 py-3 rounded-full font-semibold shadow-lg"
                data-testid="share-whatsapp-btn"
                title="Partager sur WhatsApp"
                aria-label="Partager sur WhatsApp"
              >
                <WhatsappLogo size={20} weight="fill" /> Partager
              </button>
              <Link
                to="/login"
                onClick={() => trackCTA('cta_click_login_hero')}
                className="inline-flex items-center gap-2 bg-white/95 hover:bg-white text-[#2D5A4C] px-5 py-3 rounded-full font-semibold shadow-lg border border-white/40"
                data-testid="hero-login-link"
                title="Se connecter à l'espace coopérateur"
              >
                <SignIn size={18} weight="bold" /> Espace coopérateur
              </Link>
            </div>
          </div>
          <div className="motion-fade-up motion-fade-up-delay-2">
            <TiltCard max={5} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 text-white">
              <p className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-2"><MapPin size={12} /> Territoires desservis</p>
              <ul className="mt-3 space-y-2 text-sm" data-testid="hero-territoires">
                {(data.hero?.territoires || []).map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#80C51F]" /> {t}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs italic opacity-90">« {data.hero?.footer_tagline} »</p>
              <div className="mt-5 pt-5 border-t border-white/15 flex items-center gap-3" data-testid="hero-qr-block">
                <div className="bg-white rounded-lg p-2 flex-shrink-0 shadow-lg">
                  <QRCodeSVG
                    value={typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://objectifscopoutremer.com'}
                    size={72}
                    bgColor="#ffffff"
                    fgColor="#1A2421"
                    level="M"
                    marginSize={0}
                  />
                </div>
                <div className="text-xs leading-snug">
                  <p className="flex items-center gap-1.5 font-bold uppercase tracking-widest opacity-90"><DeviceMobile size={12} weight="bold" /> Ouvrir sur mobile</p>
                  <p className="opacity-80 mt-1">Scannez pour accéder à la coopérative depuis votre téléphone.</p>
                </div>
              </div>
            </TiltCard>
          </div>
        </div>
      </ParallaxScene>

      {/* ============ Highlight ============ */}
      <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-10">
        <div className="bg-[#80C51F] text-white text-center rounded-2xl px-6 py-4 shadow-lg motion-fade-up motion-fade-up-delay-3">
          <p className="font-bold text-base md:text-lg" data-testid="landing-highlight">« {data.highlight} »</p>
        </div>
      </div>

      {/* ============ Profils PP / PM ============ */}
      <section className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-6">
        {(data.profiles || []).map((p, i) => (
          <TiltCard key={p.kind} max={4} className={`motion-fade-up motion-fade-up-delay-${i + 1}`}>
            <div className="bg-white rounded-2xl border border-[#E8E6E1] shadow-sm p-6 h-full" data-testid={`profile-${p.kind}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white shadow-md ${p.kind === 'pp' ? 'bg-[#EA3C3A]' : 'bg-[#00AEEF]'}`}>
                  {p.kind === 'pp' ? <Users size={24} weight="fill" /> : <Buildings size={24} weight="fill" />}
                </div>
                <div>
                  <h2 className="text-xl font-extrabold" style={{ fontFamily: 'Manrope' }}>{p.title}</h2>
                  <p className="text-sm text-[#5E6D66]">{p.subtitle}</p>
                </div>
              </div>
              <ul className="space-y-2 mt-4">
                {p.bullets.map((b, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm">
                    <span className="mt-1 w-4 h-4 rounded-full bg-[#FF8E00] shrink-0 flex items-center justify-center text-white text-[10px] font-bold">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              {p.college && (
                <div className="mt-5 rounded-xl border border-[#80C51F]/30 bg-[#F0F8E4] p-3">
                  <p className="text-[10px] uppercase font-bold text-[#5E6D66]">Collège d'affectation automatique</p>
                  <p className="mt-1 font-semibold text-[#2D5A4C]"><span className={`inline-block w-6 h-6 rounded-full text-center text-xs leading-6 mr-1 ${COLLEGE_TONES.G}`}>G</span> {p.college.label}</p>
                  <p className="text-xs text-[#5E6D66] italic">{p.college.note}</p>
                </div>
              )}
              {p.colleges && (
                <div className="mt-5">
                  <p className="text-[10px] uppercase font-bold text-[#5E6D66] mb-2">Collège d'affectation selon votre nature et contribution</p>
                  <ul className="space-y-1.5">
                    {p.colleges.map((c) => (
                      <li key={c.code} className="flex items-center gap-2 text-sm">
                        <span className={`inline-block w-6 h-6 rounded-full text-center text-xs leading-6 font-bold ${COLLEGE_TONES[c.code] || 'bg-gray-300'}`}>{c.code}</span>
                        <span>{c.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TiltCard>
        ))}
      </section>

      {/* ============ Avantages ============ */}
      <section className="bg-[#F0F8E4]/60 py-14 border-y border-[#E8E6E1]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-extrabold text-center text-[#2D5A4C] mb-8" style={{ fontFamily: 'Manrope' }}>
            Vos avantages et intérêts
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(data.advantages || []).map((a, i) => {
              const Icon = ICONS[a.icon] || Star;
              const colors = ['#2D5A4C', '#00AEEF', '#FF8E00', '#D5007D', '#80C51F', '#EA3C3A', '#2D5A4C', '#00AEEF'];
              const c = colors[i % colors.length];
              return (
                <TiltCard key={i} max={5} className={`motion-fade-up motion-fade-up-delay-${Math.min(i, 5)}`}>
                  <div className="bg-white rounded-2xl border border-[#E8E6E1] p-5 h-full text-center" data-testid={`advantage-${i}`}>
                    <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-white shadow" style={{ background: c }}>
                      <Icon size={26} weight="duotone" />
                    </div>
                    <p className="mt-3 font-bold text-sm uppercase tracking-wide text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{a.title}</p>
                    <p className="mt-1 text-xs text-[#5E6D66]">{a.text}</p>
                  </div>
                </TiltCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ Conventions CIPAESSE / CIPASCOP ============ */}
      <section className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-6">
        {(data.conventions || []).map((c, i) => (
          <TiltCard key={c.code} max={4}>
            <div className="bg-white rounded-2xl border border-[#E8E6E1] shadow-sm p-6 h-full motion-fade-up" data-testid={`convention-${c.code.toLowerCase()}`}>
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white shadow-md ${i === 0 ? 'bg-[#80C51F]' : 'bg-[#00AEEF]'}`}>
                  <Leaf size={22} weight="fill" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold" style={{ fontFamily: 'Manrope' }}>{c.title}</h3>
                  <p className="text-xs text-[#5E6D66]">{c.long_title}</p>
                </div>
              </div>
              <p className="mt-4 text-sm">{c.intro}</p>
              <ul className="mt-3 space-y-2 text-sm">
                {c.bullets.map((b, k) => (
                  <li key={k} className="flex items-start gap-2">
                    <span className="mt-1 w-4 h-4 rounded-full bg-[#80C51F] shrink-0 flex items-center justify-center text-white text-[10px] font-bold">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className={`mt-5 text-sm italic font-semibold rounded-lg px-3 py-2 ${i === 0 ? 'bg-[#F0F8E4] text-[#2D5A4C]' : 'bg-[#E5F5FD] text-[#005f8a]'}`}>« {c.motto} »</p>
            </div>
          </TiltCard>
        ))}
      </section>

      {/* ============ Comment devenir associé ============ */}
      <section id="comment" className="bg-white border-y border-[#E8E6E1] py-14">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-extrabold text-center text-[#2D5A4C] mb-10" style={{ fontFamily: 'Manrope' }}>
            Comment devenir associé coopérateur ?
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {(data.steps || []).map((s, i) => (
              <div key={s.n} className={`text-center motion-fade-up motion-fade-up-delay-${Math.min(i, 5)}`} data-testid={`step-${s.n}`}>
                <div className="w-12 h-12 mx-auto rounded-full bg-[#EA3C3A] text-white text-lg font-extrabold flex items-center justify-center shadow-md">{s.n}</div>
                <p className="mt-3 text-xs md:text-sm font-semibold text-[#1A2421]">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to={data.cta?.href || '/register'}
              onClick={() => trackCTA('cta_click_bottom')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF8E00] to-[#EA3C3A] hover:opacity-90 transition-opacity text-white px-8 py-4 rounded-full font-extrabold text-lg shadow-xl motion-shine"
              data-testid="join-cta-bottom"
            >
              {data.cta?.label || 'Rejoindre la coopérative'} <ArrowRight weight="bold" />
            </Link>
          </div>
          <p className="mt-4 text-center text-sm italic text-[#5E6D66]">{data.cta?.tail}</p>
          {(data.pillars || []).length > 0 && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.pillars.map((p) => (
                <div key={p} className="rounded-xl bg-[#F5F4EF] border border-[#E8E6E1] px-4 py-3 text-center text-sm font-semibold text-[#2D5A4C]">{p}</div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ Missions coopératives — animation ============ */}
      <MissionsShowcase />

      {/* ============ Bandeau ultramarin (rotation) ============ */}
      <UltramarinBanner />

      {/* ============ Footer ============ */}
      <footer className="bg-[#1A2421] text-white py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <p className="text-sm italic opacity-90 max-w-xl">{data.contact?.tagline}</p>
          <div className="text-sm space-y-1">
            {data.contact?.email && (
              <p className="flex items-center gap-2"><Envelope size={14} /> <a href={`mailto:${data.contact.email}`} className="underline hover:text-[#80C51F]">{data.contact.email}</a></p>
            )}
            {data.contact?.website && (
              <p className="flex items-center gap-2"><Globe size={14} /> <a href={`https://${data.contact.website}`} target="_blank" rel="noreferrer" className="underline hover:text-[#80C51F]">{data.contact.website}</a></p>
            )}
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs opacity-80">
          <p>&copy; {new Date().getFullYear()} Objectif SCOP Outremer — Coopérative des territoires ultramarins</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 hover:text-[#80C51F] transition-colors font-semibold"
            data-testid="public-login-link"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#80C51F] animate-pulse"></span>
            Espace coopérateur — Se connecter
          </Link>
        </div>
      </footer>
    </div>
  );
}
